import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  makeStyles,
  Tooltip,
  Button,
  Popover,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import {
  grayColor,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react.js";

import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { downloadMissingValues } from "../../viewServices/calls/apiCalls";
import {
  getFileStatusIcon,
  getFileStatusTranslation,
} from "../../viewServices/constants/statusConstants";
import VariantTagFilter from "./VariantTagFilter";

const notApplicableMap = {
  price_costbased: ["market02", "market03"],
  seek_nodes_margin: ["market04", "market05", "chain06"],
  seek_sellout_price: ["fin04", "fin05", "market03", "market04"],
};

const useStyles = makeStyles((theme) => ({
  stickyHeader: {
    position: "sticky",
    top: -1,
    zIndex: 1,
  },
  tableHeader: {
    "& *": {
      fontWeight: "300",
      backgroundColor: grayColor[13],
    },
    borderBottom: `1px solid ${grayColor[10]}`,
  },
  TableRow: {
    "&:hover": {
      backgroundColor: grayColor[12],
    },
  },
  groupNameText: {
    fontSize: "14px !important",
  },
  tableCell: {
    borderBottom: `1px solid ${grayColor[11]}`,
    padding: theme.spacing(1),
  },
  groupTableCell: {
    backgroundColor: whiteColor, // Explicitly set background
    "&:hover": {
      backgroundColor: whiteColor, // Override hover to keep it white
    },
  },
  tableDescText: {
    fontSize: "14px !important",
  },
  variantChip: {
    cursor: "pointer",
    width: "auto",
    minWidth: "80px",
    height: "27px",
    borderRadius: "16px",
    border: `1px solid ${grayColor[3]}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: "12px",
    paddingRight: "12px",
    padding: theme.spacing(0.5),
    backgroundColor: whiteColor,
    color: grayColor[2],
    "& .MuiChip-label": {
      whiteSpace: "normal",
      textAlign: "center",
      lineHeight: "1.2",
      paddingLeft: "0",
      paddingRight: "0",
    },
    "&:hover": {
      backgroundColor: grayColor[10],
      borderColor: grayColor[7],
    },
    "&.selected": {
      backgroundColor: (props) => props.clientColor,
      borderColor: (props) => props.clientColor,
      borderWidth: "1px",
      borderStyle: "solid",
      color: whiteColor,
      "&:hover": {
        borderColor: grayColor[7],
      },
    },
    "&.highlighted": {
      animation: "$highlightPulse 2s ease-in-out",
    },
  },
  "@keyframes highlightPulse": {
    "0%, 100%": {
      boxShadow: "0 0 0 rgba(25, 118, 210, 0)",
    },
    "50%": {
      boxShadow: "0 0 15px rgba(25, 118, 210, 0.6)",
    },
  },
  variantContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  statusLabel: {
    padding: "2px 2px",
    borderRadius: 12,
    fontSize: "0.7rem",
    fontWeight: 400,
    textTransform: "none",
    display: "inline-block",
    width: 64,
    textAlign: "center",
    marginRight: theme.spacing(1),
  },
  status_empty: {
    backgroundColor: grayColor[11],
    color: grayColor[6],
  },
  status_uploaded: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  },
  status_ready: {
    backgroundColor: "#e8f5e9",
    color: "#388e3c",
  },
  status_error: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
  },
  statusIcon: {
    fontSize: "1.2rem",
    verticalAlign: "middle",
    marginLeft: "10px",
  },
  checkInfoButton: {
    fontSize: "0.75rem",
    minWidth: "80px",
    padding: theme.spacing(0.5, 1),
    backgroundColor: (props) => props.clientColor,
    color: whiteColor,
    "&:hover": {
      backgroundColor: (props) => props.clientColor,
      transform: "scale(1.05)",
    },
    "&:disabled": {
      backgroundColor: grayColor[6],
      color: grayColor[2],
    },
  },
  consolidatedPercentage: {
    fontSize: "14px",
    fontWeight: "500",
    color: (props) => props.clientColor,
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  loadingIndicator: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "12px",
    color: grayColor[6],
  },
  mergeInfoPopover: {
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.3)",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    maxWidth: 400,
  },
  mergeInfoTitle: {
    fontWeight: "bold",
    fontSize: "1rem",
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
  },
  mergeInfoSection: {
    marginBottom: theme.spacing(2),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  mergeInfoLabel: {
    fontWeight: "600",
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  mergeInfoItem: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.4,
  },
}));

const getIconForStatus = (status, iconClass, t) => {
  const iconElement = getFileStatusIcon(status, iconClass, t);

  return iconElement ? (
    <Tooltip title={getFileStatusTranslation(status, t)}>{iconElement}</Tooltip>
  ) : null;
};

const SectionInputVariants = ({
  data,
  selectedVariants,
  onSelectVariant,
  clientColor,
  calculationMethod,
  mergeAnalysis,
  highlightedTables = [],
}) => {
  const classes = useStyles({ clientColor });
  const T_PREFIX = "rgm.cockpit.scenarioTable";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // State for info popover
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedMergeInfo, setSelectedMergeInfo] = useState(null);

  // State for download functionality
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);

  // State for tag filter (multiselect)
  const [tagFilters, setTagFilters] = useState([]);

  // Collect all unique tags from all variants
  const availableTags = useMemo(() => {
    const allTags = new Set();
    data.forEach((row) => {
      if (row.variants) {
        row.variants.forEach((variant) => {
          if (variant.tags) {
            variant.tags.forEach((tag) => allTags.add(tag));
          }
        });
      }
    });
    return Array.from(allTags).sort();
  }, [data]);

  // Define which tables are not applicable for each calculation method
  const getNotApplicableTables = (method) => {
    return notApplicableMap[method] || [];
  };

  const isTableNotApplicable = (tableName) => {
    const notApplicableTables = getNotApplicableTables(calculationMethod);
    return notApplicableTables.includes(tableName);
  };

  // Handle info button click for individual table
  const handleInfoClick = (event, tableId) => {
    if (!mergeAnalysis || !mergeAnalysis.consolidated_tracking) {
      return;
    }

    // Find the merge info for this table
    const tableInfo = mergeAnalysis.consolidated_tracking.find(
      (item) => item.table_id === tableId,
    );

    if (tableInfo) {
      setSelectedMergeInfo(tableInfo);
      setPopoverAnchor(event.currentTarget);
    }
  };

  // Handle popover close
  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setSelectedMergeInfo(null);
  };

  // Handle download missing values for a specific key column
  const handleDownloadMissingValues = async (tableId, keyColumn) => {
    if (!selectedVariants || isDownloading) return;

    try {
      setIsDownloading(true);
      setDownloadingKey(`${tableId}_${keyColumn}`);

      const payload = {
        selected_variants: selectedVariants,
        scenario_type: calculationMethod,
        table_id: tableId,
        key_column: keyColumn,
      };

      const blob = await downloadMissingValues(payload);

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `missing_values_${tableId}_${keyColumn}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(t("toast.download_success"));
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download file. Please try again.");
    } finally {
      setIsDownloading(false);
      setDownloadingKey(null);
    }
  };

  // Get raw tracking data for a specific table
  const getRawTrackingForTable = (tableId) => {
    if (!mergeAnalysis || !mergeAnalysis.raw_tracking) {
      return [];
    }

    // Filter raw tracking data for the specific table
    return mergeAnalysis.raw_tracking.filter(
      (item) => item.table_right === tableId,
    );
  };

  // Calculate total percentage from raw tracking data
  const calculateTotalPercentage = (rawData) => {
    if (!rawData || rawData.length === 0) return 0;

    // Sum up missing and total rows
    const totalMissing = rawData.reduce(
      (sum, item) => sum + (item.missing_left || 0),
      0,
    );
    const totalRows = rawData.reduce(
      (sum, item) => sum + (item.rows_left || 0),
      0,
    );

    return totalRows > 0 ? Math.round((totalMissing / totalRows) * 100) : 0;
  };

  // Calculate consolidated counts (missing and total) for a table
  const getConsolidatedCounts = (tableId) => {
    const rawData = getRawTrackingForTable(tableId);
    if (!rawData || rawData.length === 0) return null;

    const totalMissing = rawData.reduce(
      (sum, item) => sum + (item.missing_left || 0),
      0,
    );
    const totalRows = rawData.reduce(
      (sum, item) => sum + (item.rows_left || 0),
      0,
    );

    return { totalMissing, totalRows };
  };

  const groupedData = data.reduce((acc, row) => {
    const groupKey = row.group_level2;
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(row);
    return acc;
  }, {});

  return (
    <Table size="small">
      <TableHead className={`${classes.tableHeader} ${classes.stickyHeader}`}>
        <TableRow>
          <TableCell className={classes.tableCell} style={{ width: "10%" }}>
            {t("table.groups")}
          </TableCell>
          <TableCell className={classes.tableCell} style={{ width: "25%" }}>
            {t("table.tables")}
          </TableCell>
          <TableCell className={classes.tableCell} style={{ width: "10%" }}>
            {t("table.status")}
          </TableCell>
          <TableCell className={classes.tableCell} style={{ width: "15%" }}>
            Missing values
          </TableCell>
          <TableCell className={classes.tableCell} style={{ width: "30%" }}>
            <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {t("table.variants")}
              <VariantTagFilter
                availableTags={availableTags}
                selectedTags={tagFilters}
                onTagsChange={setTagFilters}
                clientColor={clientColor}
              />
            </Box>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Object.entries(groupedData).map(([groupName, rowsInGroup]) => {
          return rowsInGroup.map((row, rowIndex) => {
            const isFirstRowInGroup = rowIndex === 0;
            return (
              <TableRow key={row.table_id} className={classes.TableRow}>
                {isFirstRowInGroup && (
                  <TableCell
                    rowSpan={rowsInGroup.length}
                    className={`${classes.tableCell} ${classes.groupTableCell}`}
                  >
                    <Typography className={classes.groupNameText}>
                      {groupName}
                    </Typography>
                  </TableCell>
                )}
                <TableCell className={classes.tableCell}>
                  <Typography className={classes.tableDescText}>
                    {row.table_description}
                  </Typography>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {isTableNotApplicable(row.table_id) ? (
                    <Typography
                      className={classes.tableDescText}
                      style={{
                        color: "#9e9e9e",
                        marginLeft: "-15px",
                        fontStyle: "italic",
                      }}
                    >
                      {t("not_applicable")}
                    </Typography>
                  ) : (
                    getIconForStatus(row.table_status, classes.statusIcon, t)
                  )}
                </TableCell>

                {/* Missing Values Column */}
                <TableCell className={classes.tableCell}>
                  {isTableNotApplicable(row.table_id) ? (
                    <Typography
                      className={classes.tableDescText}
                      style={{ color: "#9e9e9e", fontStyle: "italic" }}
                    >
                      N/A
                    </Typography>
                  ) : (
                    (() => {
                      const counts = getConsolidatedCounts(row.table_id);
                      const missingPercentage = counts
                        ? Math.round(
                            (counts.totalMissing / counts.totalRows) * 100,
                          )
                        : null;
                      return missingPercentage !== null ? (
                        <Box
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {mergeAnalysis && (
                            <Tooltip title="View merge details">
                              <Button
                                size="small"
                                onClick={(e) =>
                                  handleInfoClick(e, row.table_id)
                                }
                                style={{
                                  minWidth: "20px",
                                  padding: "0px",
                                  marginRight: "4px",
                                }}
                              >
                                <InfoIcon
                                  fontSize="small"
                                  style={{ fontSize: "16px", color: "#666" }}
                                />
                              </Button>
                            </Tooltip>
                          )}
                          <Typography className={classes.tableDescText}>
                            {missingPercentage}%
                          </Typography>
                        </Box>
                      ) : mergeAnalysis ? (
                        <Typography
                          className={classes.tableDescText}
                          style={{ color: "#9e9e9e" }}
                        >
                          -
                        </Typography>
                      ) : (
                        <Typography
                          className={classes.tableDescText}
                          style={{ color: "#9e9e9e", fontSize: "12px" }}
                        >
                          -
                        </Typography>
                      );
                    })()
                  )}
                </TableCell>

                <TableCell className={classes.tableCell}>
                  {row.table_status !== "empty" &&
                    !isTableNotApplicable(row.table_id) && (
                      <Box className={classes.variantContainer}>
                        {row.variants
                          .filter(
                            (variant) =>
                              tagFilters.length === 0 ||
                              tagFilters.some((tag) =>
                                variant.tags?.includes(tag),
                              ),
                          )
                          .map((variant) => {
                            const isSelected =
                              selectedVariants[row.table_id] ===
                              variant.variant_id;
                            const isHighlighted =
                              highlightedTables.includes(row.table_id) &&
                              isSelected;

                            return (
                              <Chip
                                key={variant.variant_id}
                                label={variant.variant_name}
                                onClick={() =>
                                  onSelectVariant(
                                    row.table_id,
                                    variant.variant_id,
                                  )
                                }
                                className={
                                  isSelected
                                    ? `${classes.variantChip} selected ${isHighlighted ? "highlighted" : ""}`
                                    : classes.variantChip
                                }
                              />
                            );
                          })}
                      </Box>
                    )}
                </TableCell>
              </TableRow>
            );
          });
        })}
      </TableBody>

      {/* Merge Analysis Popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          className: classes.mergeInfoPopover,
        }}
        transitionDuration={300}
      >
        <Box>
          {selectedMergeInfo && (
            <>
              <Typography className={classes.mergeInfoTitle}>
                Check Raw — {selectedMergeInfo.table_id}
              </Typography>

              {(() => {
                const rawData = getRawTrackingForTable(
                  selectedMergeInfo.table_id,
                );
                const totalPercentage = calculateTotalPercentage(rawData);

                return (
                  <Box>
                    {/* Raw tracking entries */}
                    {rawData.map((item, index) => {
                      const missingPercent =
                        item.rows_left > 0
                          ? Math.round(
                              (item.missing_left / item.rows_left) * 100,
                            )
                          : 0;

                      const downloadKey = `${selectedMergeInfo.table_id}_${item.key_left}`;
                      const isDownloadingThis = downloadingKey === downloadKey;

                      return (
                        <Box
                          key={index}
                          style={{
                            marginBottom: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            className={classes.mergeInfoItem}
                            style={{
                              fontSize: "0.8rem",
                              color: "#424242",
                              flex: 1,
                            }}
                          >
                            {item.key_left}: {missingPercent}% missing (
                            {item.missing_left} of {item.rows_left})
                          </Typography>

                          {/* Download button - only show if there are missing values */}
                          {item.missing_left > 0 && (
                            <Tooltip title="Download missing rows (Excel)">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDownloadMissingValues(
                                    selectedMergeInfo.table_id,
                                    item.key_left,
                                  )
                                }
                                disabled={isDownloading}
                                style={{
                                  padding: "4px",
                                  marginLeft: "8px",
                                  color: "#9e9e9e",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isDownloading) {
                                    e.currentTarget.style.color = "#666";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = "#9e9e9e";
                                }}
                              >
                                {isDownloadingThis ? (
                                  <CircularProgress
                                    size={16}
                                    style={{ color: "#9e9e9e" }}
                                  />
                                ) : (
                                  <GetAppIcon
                                    style={{
                                      fontSize: "16px",
                                      transition: "color 0.2s ease",
                                    }}
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })}

                    {/* Total line */}
                    {rawData.length > 0 && (
                      <Typography
                        className={classes.mergeInfoItem}
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          marginTop: "12px",
                          marginBottom: "0px",
                          borderTop: "1px solid #e0e0e0",
                          paddingTop: "8px",
                          color: "#212121",
                        }}
                      >
                        Total: {totalPercentage}% missing
                      </Typography>
                    )}

                    {/* No data message */}
                    {rawData.length === 0 && (
                      <Typography
                        className={classes.mergeInfoItem}
                        style={{
                          color: "#757575",
                          fontStyle: "italic",
                          fontSize: "0.875rem",
                          textAlign: "center",
                        }}
                      >
                        No raw tracking data available
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            </>
          )}
        </Box>
      </Popover>
    </Table>
  );
};

SectionInputVariants.propTypes = {
  data: PropTypes.array.isRequired,
  selectedVariants: PropTypes.object.isRequired,
  onSelectVariant: PropTypes.func.isRequired,
  clientColor: PropTypes.string.isRequired,
  calculationMethod: PropTypes.string.isRequired,
  mergeAnalysis: PropTypes.object,
  highlightedTables: PropTypes.array,
};

export default SectionInputVariants;
